/**
 * Точки подключения экранов к оболочке. Владельцы регистрируют свои компоненты (одна строка в main.tsx), оболочка
 * сама решает, когда их показать:
 *
 * - C3 (HUD): `registerHud(Hud)` — компонент получает `snapshot` (последний снимок боя) и читает остальное из
 *   `appStore` (монеты, флаги прогресса). Без регистрации — `ui/hud/Hud` из T0.
 * - C4 (окна): `registerWindow("boostChoice", BoostChoice)` — компонент получает `route` и `close()`. Без регистрации —
 *   простое окно-заглушка с заголовком и «Дальше».
 * - C5 (вкладки): `registerTabs(Tabs)` — панель вкладок и открытая вкладка, `tab` из маршрута; открыть/закрыть —
 *   `toggleTab`/`closeTab` из стора.
 * - C6 (десктоп): `registerDesktop(Desktop)` — колонки вокруг поля при ширине от 1024 px; вкладки приходят в `tabs`.
 * - S4/I5 (вход на сайте): `registerTelegramLogin({ bot, onAuth })` — в браузере у гостя появляется виджет «Войти через Telegram».
 */
import type { AuthTelegramLoginRequest, BattleSnapshot } from "@newfungame/contracts";
import type { ComponentChildren, ComponentType } from "preact";
import { createStore } from "./store";
import type { TabId, WindowKind, WindowRoute } from "./store";

export interface HudViewProps {
  snapshot: BattleSnapshot;
}
export interface WindowViewProps<R extends WindowRoute = WindowRoute> {
  route: R;
  /** Закрыть это окно (откроется следующее в очереди). */
  close(): void;
}
export interface TabsViewProps {
  tab: TabId | null;
  /** `sheet` — нижняя панель и лист поверх боя (телефон); `panel` — постоянная панель слева (десктоп, C6). */
  mode?: "sheet" | "panel";
}
export interface DesktopViewProps {
  /** Вкладки в режиме `panel` — десктоп ставит их в левую колонку. */
  tabs: ComponentChildren;
}

export interface Slots {
  hud: ComponentType<HudViewProps> | null;
  windows: Partial<Record<WindowKind, ComponentType<WindowViewProps>>>;
  tabs: ComponentType<TabsViewProps> | null;
  /** Десктоп-раскладка (C6): колонки вокруг поля при ширине от `DESKTOP_MIN_WIDTH`. Нет — везде телефонная. */
  desktop: ComponentType<DesktopViewProps> | null;
  /** Вход через виджет Telegram Login на сайте; `null` — сервер не знает имя бота, кнопки нет. */
  telegramLogin: TelegramLoginSlot | null;
}

export interface TelegramLoginSlot {
  /** Имя бота (`TG_BOT_USERNAME` на сервере). */
  bot: string;
  /** Подписанные данные виджета; дальше — вызов `telegram-login` и слияние (`app/login.ts`). */
  onAuth: (data: AuthTelegramLoginRequest) => void;
}

export const slotStore = createStore<Slots>({ hud: null, windows: {}, tabs: null, desktop: null, telegramLogin: null });

export function registerHud(view: ComponentType<HudViewProps>): void {
  slotStore.set((s) => ({ ...s, hud: view }));
}

export function registerWindow<K extends WindowKind>(
  kind: K,
  view: ComponentType<WindowViewProps<Extract<WindowRoute, { kind: K }>>>,
): void {
  slotStore.set((s) => ({ ...s, windows: { ...s.windows, [kind]: view as ComponentType<WindowViewProps> } }));
}

export function registerTabs(view: ComponentType<TabsViewProps>): void {
  slotStore.set((s) => ({ ...s, tabs: view }));
}

export function registerDesktop(view: ComponentType<DesktopViewProps>): void {
  slotStore.set((s) => ({ ...s, desktop: view }));
}

export function registerTelegramLogin(login: TelegramLoginSlot): void {
  slotStore.set((s) => ({ ...s, telegramLogin: login }));
}
